import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Swords, Crown, Search, Edit3, X } from 'lucide-react';
import { SupabaseService } from '../services/supabaseService';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import { TacticalFormation } from './TacticalFormation';

interface QuickPlayModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const QuickPlayModal: React.FC<QuickPlayModalProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { error, success, info } = useAlert();

    const [status, setStatus] = useState<'idle' | 'searching' | 'matched'>('idle');
    const [searchTime, setSearchTime] = useState(0);
    const [armySize, setArmySize] = useState(0);
    const [hasKing, setHasKing] = useState(true);
    const [inventory, setInventory] = useState<any>(null);
    const [createdBattleId, setCreatedBattleId] = useState<string | null>(null);
    const subscriptionRef = useRef<any>(null);

    // Tactics
    const [showTactics, setShowTactics] = useState(false);
    const [blockedSquares, setBlockedSquares] = useState<number[]>([]);

    const cleanupSearch = async () => {
        if (createdBattleId) {
            console.log("Cleaning up abandoned search lobby...", createdBattleId);
            await SupabaseService.cancelQuickMatch(createdBattleId);
            setCreatedBattleId(null);
        }
        if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
    };

    useEffect(() => {
        if (!isOpen) {
            // Reset state when closed
            cleanupSearch();
            setStatus('idle');
            setSearchTime(0);
            return;
        }

        if (!user) return;

        const loadProfile = async () => {
            const profile = await SupabaseService.getProfile();
            if (profile) {
                const size = Object.values(profile.inventory).reduce((a, b) => a + (b as number), 0);
                setArmySize(size);
                setInventory(profile.inventory);
                setHasKing((profile.inventory.k || 0) > 0);
            }
        };
        loadProfile();
    }, [user, isOpen]);

    useEffect(() => {
        return () => {
            // Component Unmount Cleanup
            if (status === 'searching' && createdBattleId) {
                SupabaseService.cancelQuickMatch(createdBattleId);
            }
            if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
        };
    }, [status, createdBattleId]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (status === 'searching') {
            timer = setInterval(() => setSearchTime(t => t + 1), 1000);
        } else {
            setSearchTime(0);
        }
        return () => clearInterval(timer);
    }, [status]);

    // Polling Fallback (Robustness)
    useEffect(() => {
        let poller: NodeJS.Timeout;
        if (status === 'searching' && createdBattleId) {
            poller = setInterval(async () => {
                const battle = await SupabaseService.getBattleById(createdBattleId);
                if (battle && battle.status === 'active') {
                    console.log("Polling Update: Battle Active!", battle);
                    setStatus('matched');
                    success("Opponent Found! Deploying...");

                    if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
                    setTimeout(() => navigate(`/battle/${createdBattleId}`), 500);
                }
            }, 1000);
        }
        return () => clearInterval(poller);
    }, [status, createdBattleId, navigate, success]);

    const formatTime = (sec: number) => {
        const m = Math.floor(sec / 60).toString().padStart(2, '0');
        const s = (sec % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleStartSearch = async () => {
        if (!user) return;

        // Validation: Must have King
        if (!hasKing) {
            info("Regicide has occurred! You must recruit a King.");
            onClose();
            navigate('/barracks');
            return;
        }

        // Validation: Must have at least 8 Pawns
        if ((inventory?.p || 0) < 8) {
            info("Insufficient Infantry! You need at least 8 Pawns to form a battle line.");
            onClose();
            navigate('/barracks');
            return;
        }


        setStatus('searching');
        setSearchTime(0);

        try {
            const profile = await SupabaseService.getProfile();
            const myRating = profile.rating;
            const inventoryToUse = profile.inventory;

            // 1. Attempt to FIND a match (passing formation)
            const match = await SupabaseService.findQuickMatch(inventoryToUse, myRating, true, blockedSquares);

            if (match) {
                setStatus('matched');
                success("Opponent Found! Deploying...");
                setTimeout(() => navigate(`/battle/${match.id}`), 1500);
                return;
            }

            // 2. If no match, CREATE a lobby (passing formation)
            const newBattle = await SupabaseService.createQuickMatch(inventoryToUse, myRating, true, blockedSquares);
            if (!newBattle) throw new Error("Failed to create lobby");

            setCreatedBattleId(newBattle.id);
            console.log("Quick Match Created:", newBattle.id, "Waiting for opponent via WebSocket...");

            // 3. Subscribe to Realtime Updates (WebSocket)
            const channel = SupabaseService.subscribeToBattle(newBattle.id, (updatedBattle) => {
                if (updatedBattle.status === 'active') {
                    console.log("WebSocket Update: Battle Active!", updatedBattle);
                    setStatus('matched');
                    success("Opponent Found! Deploying...");

                    if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
                    setTimeout(() => navigate(`/battle/${newBattle.id}`), 1500);
                }
            });

            subscriptionRef.current = channel;

        } catch (err: any) {
            console.error(err);
            error("Matchmaking failed: " + err.message);
            setStatus('idle');
        }
    };

    const handleCancel = async () => {
        await cleanupSearch();
        setStatus('idle');
    };

    const closeTactics = () => setShowTactics(false);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-black border border-zinc-800 w-full max-w-6xl h-[90vh] rounded-2xl relative overflow-hidden flex flex-col shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 z-50 text-zinc-500 hover:text-white transition-colors bg-black/50 p-2 rounded-full"
                >
                    <X size={24} />
                </button>

                <div className="flex-1 overflow-y-auto bg-black text-zinc-200 font-mono p-6 relative flex flex-col">
                    {/* Background Effects */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-black to-black opacity-80 pointer-events-none" />

                    <div className="max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-[1fr,350px] gap-8 relative z-10 flex-1">
                        {/* LEFT COLUMN: STATUS CARD */}
                        <div className="bg-zinc-900/40 backdrop-blur-md border border-zinc-800 rounded-2xl p-10 flex flex-col items-center justify-center shadow-2xl relative overflow-hidden h-full min-h-[500px]">

                            {/* Idle State */}
                            {status === 'idle' && (
                                <div className="flex flex-col items-center w-full max-w-md text-center">
                                    <div className="w-32 h-32 mb-8 relative flex items-center justify-center">
                                        <div className="absolute inset-0 bg-tudor-gold/10 rounded-full blur-2xl" />
                                        <Shield className="w-16 h-16 text-emerald-600/80 mx-auto" />
                                    </div>

                                    <h2 className="text-3xl font-serif text-white mb-4">Muster Your Forces</h2>
                                    <p className="text-zinc-500 text-sm mb-10 leading-relaxed max-w-sm mx-auto">
                                        Deploy your Standing Army into immediate combat.
                                        <br />Ranked Protocol Enabled. Casualties are permanent.
                                    </p>

                                    <div className="flex flex-col gap-4 w-full">
                                        <button
                                            onClick={handleStartSearch}
                                            className="w-full bg-gradient-to-r from-tudor-gold to-[#B48E3D] text-black font-bold uppercase tracking-[0.2em] py-5 rounded-lg hover:brightness-110 active:scale-[0.98] transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)] hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] text-sm"
                                        >
                                            Initiate Search
                                        </button>

                                        <button
                                            onClick={() => setShowTactics(true)}
                                            className="w-full border border-zinc-700 hover:border-tudor-gold text-zinc-400 hover:text-tudor-gold uppercase tracking-[0.2em] py-3 rounded-lg transition-all text-xs flex items-center justify-center gap-2"
                                        >
                                            <Edit3 size={14} /> Adjust Tactics
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Searching State */}
                            {status === 'searching' && (
                                <div className="flex flex-col items-center w-full">
                                    <div className="relative w-48 h-48 mb-10 flex items-center justify-center">
                                        {/* Radar effect */}
                                        <div className="absolute inset-0 border border-tudor-gold/20 rounded-full animate-[spin_4s_linear_infinite]" />
                                        <div className="absolute inset-4 border border-tudor-gold/10 rounded-full animate-[spin_3s_linear_infinite_reverse]" />
                                        <div className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-tudor-gold/50 to-transparent animate-[spin_2s_linear_infinite]" />
                                        <Search className="w-12 h-12 text-tudor-gold animate-pulse" />
                                    </div>

                                    <h2 className="text-xl font-serif text-tudor-gold tracking-[0.2em] animate-pulse mb-3">Scanning Frequencies</h2>
                                    <div className="font-mono text-4xl text-white mb-10 tabular-nums tracking-widest text-shadow-gold">
                                        {formatTime(searchTime)}
                                    </div>

                                    <button
                                        onClick={handleCancel}
                                        className="text-zinc-600 hover:text-red-400 text-xs uppercase tracking-widest transition-colors flex items-center gap-2 group border border-zinc-800 px-4 py-2 rounded bg-black/50"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 group-hover:bg-red-400 transition-colors" />
                                        Abort Uplink
                                    </button>
                                </div>
                            )}

                            {/* Matched State */}
                            {status === 'matched' && (
                                <div className="flex flex-col items-center w-full">
                                    <div className="w-32 h-32 mb-8 relative flex items-center justify-center">
                                        <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-2xl animate-pulse" />
                                        <Swords className="w-20 h-20 text-emerald-500 relative z-10" />
                                    </div>
                                    <h2 className="text-4xl font-serif text-white tracking-wider mb-4">Target Locked</h2>
                                    <p className="text-emerald-500 uppercase tracking-widest text-sm animate-pulse">Deployment Authorization Confirmed</p>
                                </div>
                            )}
                        </div>

                        {/* RIGHT COLUMN: INTELLIGENCE */}
                        <div className="space-y-6 flex flex-col justify-center h-full">
                            {/* Mode Info */}
                            <div className="group border border-emerald-900/50 bg-emerald-950/10 rounded-xl p-6 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-3 opacity-20">
                                    <Crown className="w-16 h-16 text-emerald-500 -rotate-12" />
                                </div>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.8)] animate-pulse"></div>
                                    <h3 className="text-emerald-500 font-bold text-sm uppercase tracking-wider">Ranked Protocol</h3>
                                </div>
                                <p className="text-xs text-zinc-400 leading-relaxed relative z-10">
                                    You are entering an official ranked skirmish. Victory will improve your ELO rating and grant you gold. Defeat will result in the loss of deployed units.
                                </p>
                            </div>

                            {/* Army Stats */}
                            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-6">
                                <h3 className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold mb-4 flex items-center gap-2">
                                    <Shield size={12} /> Standing Army Status
                                </h3>
                                <div className="flex items-end gap-2 mb-2">
                                    <span className="text-3xl text-white font-mono">{armySize}</span>
                                    <span className="text-xs text-zinc-600 mb-1 uppercase">Units Ready</span>
                                </div>

                                {/* King Check Badge */}
                                {!hasKing && (
                                    <div className="mt-4 px-3 py-2 bg-red-900/20 border border-red-500/30 rounded text-red-400 text-xs flex items-center gap-2">
                                        <Crown size={12} /> King Missing - Cannot Deploy
                                    </div>
                                )}

                                <div className="w-full bg-zinc-800 h-1 rounded-full overflow-hidden mt-4">
                                    <div className="bg-tudor-gold h-full" style={{ width: '100%' }}></div>
                                </div>
                            </div>

                            {/* Server Status */}
                            <div className="p-4 bg-zinc-900/20 rounded-lg border border-zinc-800/50 text-center">
                                <div className="text-[10px] text-zinc-600 uppercase tracking-widest mb-1">Matchmaking Uplink</div>
                                <div className="flex items-center justify-center gap-2 text-emerald-500 text-xs font-bold">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                                    Connected
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TACTICS MODAL */}
                    {showTactics && inventory && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
                            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-2xl w-full relative shadow-2xl">
                                <button
                                    onClick={closeTactics}
                                    className="absolute top-4 right-4 text-zinc-500 hover:text-white"
                                >
                                    <span className="text-2xl">&times;</span>
                                </button>

                                <TacticalFormation
                                    inventory={inventory}
                                    side="white" // Preview as White for simplicity
                                    blockedSquares={blockedSquares}
                                    setBlockedSquares={setBlockedSquares}
                                />

                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={closeTactics}
                                        className="bg-tudor-gold text-black font-bold uppercase tracking-widest px-6 py-2 rounded hover:brightness-110"
                                    >
                                        Confirm Tactics
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
